import React, {
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
	type ComponentType,
	type RefObject,
} from 'react';

// eslint-disable-next-line import/no-cycle
import { ContentOwnershipDialog } from './ContentOwnershipDialog.tsx';

type OpenModalArgs = {
	returnFocusRef?: RefObject<HTMLElement>;
};

export type ContentOwnershipContextType = {
	isModalOpen: boolean;
	openModal: (args?: OpenModalArgs) => void;
	closeModal: () => void;
};
const ContentOwnershipContext = createContext<ContentOwnershipContextType>({
	isModalOpen: false,
	openModal: () => {
		/* no-op */
	},
	closeModal: () => {
		/* no-op */
	},
});

type ContentOwnershipContextProviderProps = {
	children: React.ReactNode;
};
export const ContentOwnershipContextProvider = ({
	children,
}: ContentOwnershipContextProviderProps) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [returnFocusRef, setReturnFocusRef] = useState<RefObject<HTMLElement> | undefined>();

	const openModal = useCallback((args?: OpenModalArgs) => {
		setIsModalOpen(true);
		if (args?.returnFocusRef) {
			setReturnFocusRef(args.returnFocusRef);
		}
	}, []);
	const closeModal = useCallback(() => setIsModalOpen(false), []);

	const contentOwnershipContextValue = useMemo(
		() => ({
			isModalOpen,
			openModal,
			closeModal,
		}),
		[isModalOpen, openModal, closeModal],
	);

	return (
		<ContentOwnershipContext.Provider value={contentOwnershipContextValue}>
			<>
				{children}
				<ContentOwnershipDialog shouldReturnFocusRef={returnFocusRef} />
			</>
		</ContentOwnershipContext.Provider>
	);
};

export const withContentOwnershipContextProvider =
	<P extends object>(WrappedComponent: ComponentType<P>) =>
	(props: P) => (
		<ContentOwnershipContextProvider>
			<WrappedComponent {...props} />
		</ContentOwnershipContextProvider>
	);

export const useContentOwnershipContext = () => {
	return useContext(ContentOwnershipContext);
};

export const ContentOwnershipContextConsumer = ({ children }) => {
	const context = useContentOwnershipContext();
	return children(context);
};
